import {
  Campaign,
  Status_Enum as Status,
  UpdateCampaignRequest,
} from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import dayjs from 'dayjs';
import { formatDate, getTodaysDate } from '@frontend/date';
import { DATE_FORMAT, MONTH_FORMAT, TIME_FORMAT } from './constants';

// TODO: Break these utils out into their own files with unit tests.

/** Gets the next time in half hour increments that's at least 31 minutes to an hour in the future.
 * @param forDate The MM/DD/YYYY formatted date string used for calculating the minimum time.
 * @param startingAt The 0-23 hour minimum, starting time. Defaults to 7:00 am.
 * @param endingAt The 0-23 hour maximum, ending time. Defaults to 10:00 am.
 * @param now The time right now, used for comparisons and time formatting. Don't supply this unless testing.
 */
export const getNextMinTime = (forDate?: string, startingAt = 7, endingAt = 22, now = dayjs()) => {
  const isToday = forDate === formatDate(now, DATE_FORMAT);
  const nextMinTime = now.add(60 - (now.minute() % 30), 'minutes');
  const startingTime = now.hour(startingAt).minute(0);
  const isAfterStart = now.hour() >= startingAt - 1;
  const isBeforeEnd = now.isSame(nextMinTime, 'day') && (endingAt === undefined || nextMinTime.hour() < endingAt);
  return formatDate(isToday && isAfterStart && isBeforeEnd ? nextMinTime : startingTime, TIME_FORMAT);
};

/**
 * Gets the month name that corresponds to the given 1-based month number
 * @param month The 1-based month number
 * @returns The full month name
 */
export const getMonthFromNumber = (month?: number) =>
  month ? formatDate(dayjs().month(month - 1), MONTH_FORMAT) : getTodaysDate(MONTH_FORMAT);

/**
 * Creates an update request to change a campaign's status
 * @param orgId The organization ID that owns the campaign
 * @param campaign The campaign object to update
 * @param status The new status to set for the campaign
 * @returns An UpdateCampaignRequest object with the campaign status updated and proper update mask
 */
export const createCampaignStatusUpdateRequest = (
  orgId: string,
  status: Status,
  campaign: Campaign
): UpdateCampaignRequest => {
  const campaignPaths = ['campaign.currentStatus'];
  return {
    orgId,
    campaign: {
      ...campaign,
      currentStatus: status,
    },
    updateMask: {
      paths: campaignPaths,
      toJSON: () => campaignPaths.join(','),
    },
  };
};

export const checkIsUnscheduled = (currentStatus?: Status) =>
  currentStatus === Status.DRAFT || currentStatus === Status.HIDDEN_DRAFT || currentStatus === Status.UNSPECIFIED;

/**
 * Bridges a full-array selection (e.g. from OrgLocationPicker) to a form field whose `onChange` TOGGLES a
 * single id at a time: fires `toggleId` only for the ids that changed between `current` and `next`, and
 * returns the de-duped `next` array (handy for keeping a separate source-of-truth in sync).
 */
export const toggleChangedIds = (current: string[], next: string[], toggleId: (id: string) => void): string[] => {
  const unique = [...new Set(next)];
  const currentSet = new Set(current);
  const nextSet = new Set(unique);
  [...current.filter((id) => !nextSet.has(id)), ...unique.filter((id) => !currentSet.has(id))].forEach(toggleId);
  return unique;
};
